import React from "react";
import {
  Plus, Sparkles, ChevronRight, Loader2, AlertCircle, TrendingUp,
} from "lucide-react";
import { TEMP_META } from "./constants";
import { TempProspectRow } from "./PipelineRows";

export function PipelineListView({
  items, grouped, tempCounts, analysis, listAnalyzing, runListAnalysis,
  newThisWeek, navigate, setAddOpen,
}) {
  if (items.length === 0) {
    return (
      <div className="px-5 mt-4">
        <div className="hp-card p-10 text-center">
          <div className="w-14 h-14 rounded-xl bg-[var(--surface-alt)] border border-[var(--border)] grid place-items-center mx-auto">
            <Plus className="w-5 h-5 text-zinc-500" strokeWidth={1.5} />
          </div>
          <div className="font-display font-semibold text-[var(--text)] mt-4">Votre base est vide</div>
          <div className="text-sm text-zinc-500 mt-1">
            Ajoutez un premier lead — SIRET, carte de visite, ou saisie rapide.
          </div>
          <button onClick={() => setAddOpen(true)} className="hp-btn hp-btn-primary mt-5" data-testid="empty-add-prospect">
            <Plus className="w-3.5 h-3.5" /> Ajouter un lead
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Weekly indicator */}
      <div className="px-5">
        <div className="p-4 flex items-center gap-3 rounded-xl border border-[var(--border)]"
          style={{ background: "linear-gradient(135deg, rgba(0,220,130,0.15), rgba(56,189,248,0.05))" }}>
          <div className="w-11 h-11 rounded-lg bg-emerald-500/20 border border-emerald-500/40 grid place-items-center shrink-0">
            <TrendingUp className="w-5 h-5 text-emerald-300" strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="hp-label">Cette semaine</div>
            <div className="font-display text-xl font-semibold text-white leading-tight tabular-nums">+{newThisWeek} leads</div>
          </div>
        </div>
      </div>

      {/* AI analysis */}
      <div className="px-5 mt-4">
        {!analysis ? (
          <button
            onClick={runListAnalysis}
            disabled={listAnalyzing}
            data-testid="run-analysis"
            className="w-full hp-card p-4 flex items-center gap-3 active:scale-[0.99] transition-colors disabled:opacity-70 hover:border-zinc-700"
          >
            <div className="w-11 h-11 rounded-lg bg-[var(--surface-alt)] border border-[var(--border)] grid place-items-center shrink-0">
              {listAnalyzing ? <Loader2 className="w-5 h-5 animate-spin text-emerald-400" /> : <Sparkles className="w-5 h-5 text-emerald-400" strokeWidth={1.8} />}
            </div>
            <div className="flex-1 text-left">
              <div className="font-semibold text-sm text-[var(--text)]">
                {listAnalyzing ? "Analyse en cours…" : "Analyser ma base"}
              </div>
              <div className="text-xs text-zinc-500">Leads potentiels et conseils</div>
            </div>
            <ChevronRight className="w-5 h-5 text-zinc-500" />
          </button>
        ) : (
          <div className="p-5 rounded-xl border border-emerald-500/40"
            style={{ background: "linear-gradient(135deg, rgba(0,220,130,0.22), rgba(0,220,130,0.08))" }}
            data-testid="analysis-result">
            <div className="flex items-center gap-2 hp-label" style={{ color: "#86efac" }}>
              <Sparkles className="w-3.5 h-3.5" /> Analyse Prosperr
            </div>
            <div className="font-display text-4xl font-semibold mt-3 leading-none tracking-tight text-white tabular-nums">{analysis.potential_leads}</div>
            <div className="text-sm text-zinc-300 mt-1 font-medium">leads potentiels</div>
            {analysis.headline && <div className="mt-3 text-sm leading-relaxed text-zinc-400">{analysis.headline}</div>}
          </div>
        )}
      </div>

      {/* Temperature breakdown */}
      <div className="px-5 mt-5">
        <div className="hp-label mb-3 px-1">Classement</div>
        <div className="grid grid-cols-2 gap-3">
          {["qualifie", "chaud", "tiede", "froid"].map((k) => {
            const m = TEMP_META[k];
            const Icon = m.icon;
            return (
              <a
                key={k}
                href={`#group-${k}`}
                data-testid={`temp-${k}`}
                className={`rounded-xl p-4 border ${m.bg} ${m.border} active:scale-[0.98] transition-transform`}
              >
                <div className={`w-9 h-9 rounded-lg border ${m.border} grid place-items-center`} style={{ background: "rgba(9,9,11,0.4)" }}>
                  <Icon className={`w-4 h-4 ${m.color}`} strokeWidth={1.8} />
                </div>
                <div className="font-display text-3xl font-semibold text-white mt-3 leading-none tabular-nums">{tempCounts[k]}</div>
                <div className={`text-sm font-medium mt-1 ${m.color}`}>{m.label}</div>
              </a>
            );
          })}
        </div>
      </div>

      {/* Recommendations + Risks */}
      {analysis && (analysis.recommendations?.length > 0 || analysis.risks?.length > 0) && (
        <div className="px-5 mt-5 space-y-4">
          {analysis.recommendations?.length > 0 && (
            <div>
              <div className="hp-label mb-2 px-1 flex items-center gap-1.5" style={{ color: "#86efac" }}>
                <Sparkles className="w-3.5 h-3.5" /> Recommandations
              </div>
              <div className="hp-card overflow-hidden divide-y divide-[var(--border-soft)]">
                {analysis.recommendations.map((r, i) => (
                  <div key={`rec-${i}-${r.slice(0, 10)}`} className="flex items-start gap-3 p-4">
                    <div className="w-7 h-7 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 grid place-items-center shrink-0 mt-0.5 text-xs font-semibold tabular-nums">{i + 1}</div>
                    <div className="text-sm text-zinc-300 leading-snug flex-1">{r}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {analysis.risks?.length > 0 && (
            <div>
              <div className="hp-label mb-2 px-1 flex items-center gap-1.5" style={{ color: "#fca5a5" }}>
                <AlertCircle className="w-3.5 h-3.5" /> À surveiller
              </div>
              <div className="hp-card overflow-hidden divide-y divide-[var(--border-soft)]">
                {analysis.risks.map((r, i) => (
                  <div key={`risk-${i}-${r.slice(0, 10)}`} className="flex items-start gap-3 p-4">
                    <AlertCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
                    <div className="text-sm text-zinc-300 leading-snug flex-1">{r}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Groups by temperature */}
      <div className="px-5 mt-6 space-y-6">
        {["qualifie", "chaud", "tiede", "froid"].map((k) => {
          const list = grouped[k];
          const m = TEMP_META[k];
          if (list.length === 0) return null;
          return (
            <div key={k} id={`group-${k}`}>
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${m.border.replace("border-", "bg-").replace("/40", "")}`} style={{ background: "currentColor" }} />
                  <div className={`text-sm font-medium ${m.color}`}>{m.label}</div>
                  <div className="text-sm text-zinc-500 tabular-nums">· {list.length}</div>
                </div>
              </div>
              <div className="hp-card overflow-hidden divide-y divide-[var(--border-soft)]">
                {list.map((p) => (
                  <TempProspectRow
                    key={p.prospect_id}
                    p={p}
                    onClick={() => navigate(`/prospect/${p.prospect_id}`)}
                    navigate={navigate}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
